import classNames from 'classnames';
import { Check } from 'lucide-react';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

import { Tooltip } from '../ui/Tooltip';

import styles from './styles.module.css';

import { track } from '@/shared/analytics/track';
import { useAuth } from '@/shared/hooks/useAuth';
import { useBrandStore } from '@/shared/stores/useBrandStore';
import { getBrandInitials } from '@/shared/utils/initials';

/**
 * Brand scope, in the sidebar footer directly beneath the organization.
 *
 * Brand is GLOBAL scope, not a property of whatever record is on screen: the
 * selected brand filters Reports, Analytics, Cashout and Dropshipping, and
 * BrandGuard blocks two routes until one is chosen. It used to live in a top
 * bar, which meant the same value was editable in two places on the Cashout
 * screen. The top bar is gone; this is the only switcher.
 *
 * Sits between the org and the user avatar so the footer reads as one
 * hierarchy: organization -> brand -> you.
 *
 * Deliberately a plain menu rather than react-select — react-select's control
 * chrome is form furniture, and this is navigation.
 */
const SidebarBrandSwitcher: React.FC = () => {
  const { user } = useAuth();
  const { brandId, setBrandId } = useBrandStore();
  const [isOpen, setIsOpen] = useState(false);
  const [menuStyle, setMenuStyle] = useState<React.CSSProperties>({});
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  const brands = useMemo(() => user?.brands ?? [], [user?.brands]);
  const selected = brands.find((b) => b.id === brandId) ?? null;

  useEffect(() => {
    if (!isOpen) return undefined;
    const onDown = (e: MouseEvent) => {
      if (
        !menuRef.current?.contains(e.target as Node) &&
        !triggerRef.current?.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [isOpen]);

  // No brands at all is a supported state (every self-serve org starts there),
  // and the pages such an org can reach reference no brandId.
  if (brands.length === 0) return null;

  const label = selected?.name ?? 'Select a brand';
  const initials = selected ? getBrandInitials(selected.name) : '—';

  const openMenu = () => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (rect) {
      setMenuStyle({
        position: 'fixed',
        left: rect.right + 8,
        bottom: Math.max(window.innerHeight - rect.bottom, 8),
        width: 240
      });
    }
    setIsOpen((v) => !v);
  };

  const choose = (id: string, name: string) => {
    setIsOpen(false);
    if (id === brandId) return;
    track('brand_switched', {
      from_brand_id: brandId,
      from_brand_name: selected?.name,
      to_brand_id: id,
      to_brand_name: name
    });
    setBrandId(id);
  };

  // A single brand is still scope the client needs to see, but a menu with one
  // option is a dead control — render it as static context instead.
  if (brands.length === 1) {
    return (
      <Tooltip content={`Brand: ${label}`} position="right">
        <div aria-label={`Brand: ${label}`} className={styles.static}>
          <span aria-hidden={true} className={styles.avatar}>
            {initials}
          </span>
        </div>
      </Tooltip>
    );
  }

  return (
    <>
      <Tooltip content={`Brand: ${label}. Switch brand`} position="right">
        <button
          aria-expanded={isOpen}
          aria-haspopup="menu"
          aria-label={`Brand: ${label}. Switch brand`}
          className={classNames(styles.trigger, { [styles.open]: isOpen })}
          onClick={openMenu}
          ref={triggerRef}
          type="button"
        >
          <span aria-hidden={true} className={styles.avatar}>
            {initials}
          </span>
        </button>
      </Tooltip>

      {isOpen &&
        createPortal(
          <div
            className={styles.menu}
            ref={menuRef}
            role="menu"
            style={menuStyle}
          >
            <p className={styles.menuHeading}>Brand</p>
            {brands.map((b) => (
              <button
                className={classNames(styles.menuItem, {
                  [styles.menuItemActive]: b.id === brandId
                })}
                key={b.id}
                onClick={() => choose(b.id, b.name)}
                role="menuitemradio"
                aria-checked={b.id === brandId}
                type="button"
              >
                <span className={styles.menuItemLabel}>{b.name}</span>
                {b.id === brandId && <Check aria-hidden={true} size={14} />}
              </button>
            ))}
          </div>,
          document.body
        )}
    </>
  );
};

export default SidebarBrandSwitcher;
